import React, { useEffect } from 'react';
import styled from 'styled-components';
import useStatisticsStore from '../store/statisticsStore';
import Loader from './Loader';

const StatsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
`;

const PageTitle = styled.h2`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 600;
  font-size: 28px;
  line-height: 1.3;
  color: #FFFFFF;
  margin: 0;
`;

const StatsRow = styled.div`
  display: flex;
  justify-content: stretch;
  align-items: stretch;
  width: 100%;
  gap: 32px;
  
  @media (max-width: 1200px) {
    flex-direction: column;
  }
`;

const MainMetricCard = styled.div`
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #24BA7D 0%, #1C9162 100%);
  border-radius: 24px;
  padding: 32px;
  flex: 1;
  gap: 12px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100px;
    height: 100px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    transform: translate(30px, -30px);
  }
`;

const SecondaryMetricCard = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #172A2A;
  border: 1px solid #1F3737;
  border-radius: 24px;
  padding: 32px;
  flex: 1;
  gap: 12px;
  transition: all 0.3s ease;

  &:hover {
    border-color: #24BA7D;
    transform: translateY(-4px);
    box-shadow: 0 8px 32px rgba(36, 186, 125, 0.15);
  }
`;

const MetricValue = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 700;
  font-size: 32px;
  line-height: 1.2;
  color: #FFFFFF;
  z-index: 1;
`;

const MetricLabel = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.8);
  z-index: 1;
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #172A2A;
  border-radius: 24px;
  padding: 32px;
  gap: 24px;
  flex: 1;
`;

const SectionTitle = styled.h3`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 600;
  font-size: 20px;
  line-height: 1.3;
  color: #FFFFFF;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TopList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 400px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #1F3737;
    border-radius: 3px;
    margin: 4px 0;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #24BA7D;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #1C9162;
  }
`;

const TopItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background-color: #1F3737;
  border-radius: 12px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #24403F;
    transform: translateX(4px);
  }
`;

const TopItemInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
`;

const TopItemRank = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #24BA7D 0%, #1C9162 100%);
  border-radius: 50%;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 700;
  font-size: 14px;
  color: #FFFFFF;
  flex-shrink: 0;
`;

const TopItemName = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 1.5;
  color: #FFFFFF;
  flex: 1;
`;

const TopItemValue = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const TopItemCount = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 1.33;
  color: #24BA7D;
`;

const TopItemPercent = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.33;
  color: #609F94;
`;

const GridLayout = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 32px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DetailedTable = styled.div`
  background-color: #172A2A;
  border-radius: 24px;
  padding: 32px;
`;

const TableContainer = styled.div`
  width: 100%;
  max-height: 500px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #1F3737;
    border-radius: 3px;
    margin: 4px 0;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #24BA7D;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #1C9162;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const THead = styled.thead`
  background-color: #172A2A;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const TBody = styled.tbody``;

const TR = styled.tr`
  &:hover {
    background-color: rgba(31, 55, 55, 0.5);
  }
`;

const TH = styled.th`
  padding: 16px;
  text-align: left;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.43;
  color: #609F94;
  border-bottom: 1px solid #1F3737;
  white-space: nowrap;
`;

const TD = styled.td`
  padding: 16px;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.43;
  color: #FFFFFF;
  border-bottom: 1px solid #1F3737;
  white-space: nowrap;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  background-color: #172A2A;
  border-radius: 24px;
  text-align: center;
`;

const EmptyStateText = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 18px;
  color: #609F94;
  margin-top: 16px;
`;

const InventoryAnalytics: React.FC = () => {
  const { inventoryData, loading, error, fetchInventoryAnalytics } = useStatisticsStore();

  useEffect(() => {
    if (!inventoryData && !loading) {
      fetchInventoryAnalytics();
    }
  }, []);

  if (loading) {
    return (
      <EmptyState>
        <Loader text="Загрузка данных инвентаря..." size="medium" noPadding />
      </EmptyState>
    );
  }

  if (error) {
    return (
      <EmptyState>
        <EmptyStateText>Ошибка: {error}</EmptyStateText>
      </EmptyState>
    );
  }

  if (!inventoryData) {
    return (
      <EmptyState>
        <EmptyStateText>Данные инвентаря недоступны</EmptyStateText>
      </EmptyState>
    );
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ru-RU').format(price);
  };

  const getTopItems = (items: any[], limit?: number) => {
    const sorted = items.sort((a, b) => b.items_count - a.items_count);
    return limit ? sorted.slice(0, limit) : sorted;
  };

  const calculatePercent = (value: number, total: number) => {
    return ((value / total) * 100).toFixed(1);
  };

  return (
    <StatsContainer>
      {/* Заголовок страницы */}
      <PageHeader>
        <PageTitle>Статистика инвентаря</PageTitle>
      </PageHeader>

      {/* Основные метрики */}
      <StatsRow>
        <MainMetricCard>
          <MetricValue>{inventoryData.all_items_count.toLocaleString()}</MetricValue>
          <MetricLabel>Всего товаров в инвентаре</MetricLabel>
        </MainMetricCard>
        
        <SecondaryMetricCard>
          <MetricValue>{formatPrice(inventoryData.user_price_inventory)} ₽</MetricValue>
          <MetricLabel>Ваша цена инвентаря</MetricLabel>
        </SecondaryMetricCard>
        
        <SecondaryMetricCard>
          <MetricValue>{formatPrice(inventoryData.market_price_inventory)} ₽</MetricValue>
          <MetricLabel>Рыночная стоимость</MetricLabel>
        </SecondaryMetricCard>
      </StatsRow>

      {/* Топ списки */}
      <GridLayout>
        {/* Популярные размеры */}
        <SectionContainer>
          <SectionTitle>Популярные размеры</SectionTitle>
          <TopList>
            {getTopItems(inventoryData.size_inventory).map((item, index) => (
              <TopItem key={item.us_size}>
                <TopItemInfo>
                  <TopItemRank>{index + 1}</TopItemRank>
                  <TopItemName>US {item.us_size}</TopItemName>
                </TopItemInfo>
                <TopItemValue>
                  <TopItemCount>{item.items_count} шт.</TopItemCount>
                  <TopItemPercent>{calculatePercent(item.items_count, inventoryData.all_items_count)}%</TopItemPercent>
                </TopItemValue>
              </TopItem>
            ))}
          </TopList>
        </SectionContainer>

        {/* Ведущие бренды */}
        <SectionContainer>
          <SectionTitle>Ведущие бренды</SectionTitle>
          <TopList>
            {getTopItems(inventoryData.brand_inventory).map((item, index) => (
              <TopItem key={item.brand}>
                <TopItemInfo>
                  <TopItemRank>{index + 1}</TopItemRank>
                  <TopItemName>{item.brand}</TopItemName>
                </TopItemInfo>
                <TopItemValue>
                  <TopItemCount>{item.items_count} товаров</TopItemCount>
                  <TopItemPercent>{calculatePercent(item.items_count, inventoryData.all_items_count)}%</TopItemPercent>
                </TopItemValue>
              </TopItem>
            ))}
          </TopList>
        </SectionContainer>

        {/* Топ модели */}
        <SectionContainer>
          <SectionTitle>Топ модели</SectionTitle>
          <TopList>
            {getTopItems(inventoryData.model_inventory).map((item, index) => (
              <TopItem key={`${item.brand}-${item.model}`}>
                <TopItemInfo>
                  <TopItemRank>{index + 1}</TopItemRank>
                  <TopItemName>{item.model}</TopItemName>
                </TopItemInfo>
                <TopItemValue>
                  <TopItemCount>{item.items_count} шт.</TopItemCount>
                  <TopItemPercent>{calculatePercent(item.items_count, inventoryData.all_items_count)}%</TopItemPercent>
                </TopItemValue>
              </TopItem>
            ))}
          </TopList>
        </SectionContainer>

        <SectionContainer>
          <SectionTitle>Лидирующие артикулы</SectionTitle>
          <TopList>
            {getTopItems(inventoryData.article_inventory).map((item, index) => (
              <TopItem key={item.article}>
                <TopItemInfo>
                  <TopItemRank>{index + 1}</TopItemRank>
                  <TopItemName>{item.article}</TopItemName>
                </TopItemInfo>
                <TopItemValue>
                  <TopItemCount>{item.items_count} ед.</TopItemCount>
                  <TopItemPercent>{calculatePercent(item.items_count, inventoryData.all_items_count)}%</TopItemPercent>
                </TopItemValue>
              </TopItem>
            ))}
          </TopList>
        </SectionContainer>
      </GridLayout>

      {/* Детальная таблица артикулов и размеров */}
      {inventoryData.article_and_size_inventory && inventoryData.article_and_size_inventory.length > 0 && (
        <DetailedTable>
          <SectionTitle style={{ marginBottom: '24px' }}>Детализация по артикулам и размерам</SectionTitle>
          <TableContainer>
            <Table>
              <THead>
                <TR>
                  <TH>№</TH>
                  <TH>Артикул</TH>
                  <TH>Размер</TH>
                  <TH>Количество</TH>
                </TR>
              </THead>
              <TBody>
                {inventoryData.article_and_size_inventory.map((item, index) => (
                  <TR key={`${item.article}-${item.us_size}`}>
                    <TD>{index + 1}</TD>
                    <TD>{item.article}</TD>
                    <TD>US {item.us_size}</TD>
                    <TD>{item.items_count} шт.</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableContainer>
        </DetailedTable>
      )}
    </StatsContainer>
  );
};

export default InventoryAnalytics; 